import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { fileHref } from "./common";

// Content approvals tab — four stacked queues: new events, event edits,
// profile edits (vendor + event-creator profile revisions), and marketplace
// listings. Each queue shares the same approve/reject row pattern but surfaces
// enough context on the summary card for the admin to triage without opening
// the full detail dialog.
export default function ContentApprovalsTab({
  pendingContent,
  reviewEvent,
  reviewEventEdit,
  reviewProfileEdit,
  reviewListing,
  openContentDetail,
}) {
  const openOnKey = (ev, type, data) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      openContentDetail(type, data);
    }
  };

  const eventEdits = pendingContent.event_edits || [];
  const profileEdits = pendingContent.profile_edits || [];

  return (
    <div className="space-y-6" data-testid="content-approvals-tab">
      <div>
        <h3 className="font-heading font-bold text-forest mb-3">Pending events</h3>
        {pendingContent.events.length === 0 ? (
          <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="no-pending-events">No events awaiting review.</div>
        ) : pendingContent.events.map((e) => {
          const eventPhoto = e.event_photo || e.image_url;
          return (
            <div
              key={e.event_id}
              className="bg-white/80 border border-sage/20 rounded-2xl p-5 mb-3 shadow-sm cursor-pointer hover:border-sage/50 hover:shadow-md transition"
              data-testid={`pending-event-${e.event_id}`}
              onClick={() => openContentDetail("event", e)}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => openOnKey(ev, "event", e)}
            >
              <div className="flex flex-wrap gap-4">
                {eventPhoto && (
                  <img src={fileHref(eventPhoto)} alt={e.event_name || e.title} className="w-32 h-32 rounded-2xl object-cover border border-sage/20 shrink-0" data-testid={`pending-event-photo-${e.event_id}`} />
                )}
                <div className="flex-1 min-w-[240px] space-y-1.5">
                  <div className="flex items-center gap-3">
                    {e.company_logo && (
                      <img src={fileHref(e.company_logo)} alt="Logo" className="w-10 h-10 rounded-xl object-cover border border-sage/20" data-testid={`pending-event-logo-${e.event_id}`} />
                    )}
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50" data-testid={`pending-event-organizer-${e.event_id}`}>{e.organizer_name || e.creator_name}</p>
                      <p className="font-bold text-forest" data-testid={`pending-event-name-${e.event_id}`}>{e.event_name || e.title}</p>
                    </div>
                  </div>
                  {(e.about_event || e.description) && (
                    <p className="text-sm text-forest/70 leading-relaxed line-clamp-2" data-testid={`pending-event-about-${e.event_id}`}>{e.about_event || e.description}</p>
                  )}
                  <p className="text-[11px] text-sage-dark/70 font-semibold mt-2">Tap card to view full event →</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0" onClick={(ev) => ev.stopPropagation()}>
                  <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => reviewEvent(e.event_id, "approve")} data-testid={`approve-event-${e.event_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => reviewEvent(e.event_id, "reject")} data-testid={`reject-event-${e.event_id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <h3 className="font-heading font-bold text-forest mb-3">Pending event edits</h3>
        {eventEdits.length === 0 ? (
          <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="no-pending-event-edits">No event edits awaiting review.</div>
        ) : eventEdits.map((ed) => {
          const p = ed.proposed || {};
          const cur = ed.current || {};
          const row = (label, was, now) => (
            <div className="grid grid-cols-[110px_1fr_1fr] gap-2 text-xs py-1 border-b border-sage/10 last:border-b-0" data-testid={`edit-row-${ed.edit_id}-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="font-bold uppercase text-forest/50 tracking-wide">{label}</span>
              <span className="text-forest/50 line-through">{was || "—"}</span>
              <span className="text-forest">{now || "—"}</span>
            </div>
          );
          return (
            <div
              key={ed.edit_id}
              className="bg-white/80 border border-sage/20 rounded-2xl p-5 mb-3 shadow-sm cursor-pointer hover:border-sage/50 hover:shadow-md transition"
              data-testid={`pending-event-edit-${ed.edit_id}`}
              onClick={() => openContentDetail("event_edit", ed)}
              role="button"
              tabIndex={0}
              onKeyDown={(ev) => openOnKey(ev, "event_edit", ed)}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">Edit for</p>
                  <p className="font-bold text-forest">{ed.event_title_current}</p>
                  <p className="text-xs text-forest/50">Submitted by {ed.submitted_name}</p>
                  <p className="text-[11px] text-sage-dark/70 font-semibold mt-2">Tap card to view full diff →</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0" onClick={(ev) => ev.stopPropagation()}>
                  <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => reviewEventEdit(ed.edit_id, "approve")} data-testid={`approve-edit-${ed.edit_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => reviewEventEdit(ed.edit_id, "reject")} data-testid={`reject-edit-${ed.edit_id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                </div>
              </div>
              <div className="rounded-xl bg-softstone/40 p-3">
                <div className="grid grid-cols-[110px_1fr_1fr] gap-2 text-[10px] font-bold uppercase tracking-wide text-forest/40 pb-1 border-b border-sage/15">
                  <span>Field</span><span>Current</span><span>Proposed</span>
                </div>
                {row("Organizer", cur.organizer_name, p.organizer_name)}
                {row("Event", cur.event_name || cur.title, p.event_name)}
                {row("Date", cur.event_date || cur.date, p.event_date)}
                {row("Location", cur.location, p.location)}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <h3 className="font-heading font-bold text-forest mb-3">Pending profile edits</h3>
        {profileEdits.length === 0 ? (
          <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="no-pending-profile-edits">No profile edits awaiting review.</div>
        ) : profileEdits.map((pe) => {
          const p = pe.proposed || {};
          const cur = pe.current || {};
          const roleLabel = pe.role === "vendor" ? "Vendor" : pe.role === "event_creator" ? "Event Creator" : (pe.role || "");
          const row = (label, was, now) => (
            <div className="grid grid-cols-[110px_1fr_1fr] gap-2 text-xs py-1 border-b border-sage/10 last:border-b-0">
              <span className="font-bold uppercase text-forest/50 tracking-wide">{label}</span>
              <span className="text-forest/50 line-through break-words">{was || "—"}</span>
              <span className="text-forest break-words">{now || "—"}</span>
            </div>
          );
          return (
            <div
              key={pe.edit_id}
              className="bg-white/80 border border-sage/20 rounded-2xl p-5 mb-3 shadow-sm"
              data-testid={`pending-profile-edit-${pe.edit_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">{roleLabel} profile edit</p>
                  <p className="font-bold text-forest">{pe.business_name_current || p.business_name || p.organizer_name || pe.user_name}</p>
                  <p className="text-xs text-forest/50">Submitted by {pe.user_name}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => reviewProfileEdit(pe.edit_id, "approve")} data-testid={`approve-profile-edit-${pe.edit_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => reviewProfileEdit(pe.edit_id, "reject")} data-testid={`reject-profile-edit-${pe.edit_id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                </div>
              </div>
              <div className="rounded-xl bg-softstone/40 p-3">
                <div className="grid grid-cols-[110px_1fr_1fr] gap-2 text-[10px] font-bold uppercase tracking-wide text-forest/40 pb-1 border-b border-sage/15">
                  <span>Field</span><span>Current</span><span>Proposed</span>
                </div>
                {row("Business", cur.business_name || cur.organizer_name, p.business_name || p.organizer_name)}
                {row("Tagline", cur.tagline, p.tagline)}
                {row("Description", cur.description, p.description)}
                {row("Location", cur.location, p.location)}
                {row("Phone", cur.phone || cur.contact_number, p.phone || p.contact_number)}
                {row("Email", cur.contact_email || cur.email, p.contact_email || p.email)}
                {row("Website", cur.website, p.website)}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <h3 className="font-heading font-bold text-forest mb-3">Pending marketplace listings</h3>
        {pendingContent.listings.length === 0 ? (
          <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="no-pending-listings">No listings awaiting review.</div>
        ) : pendingContent.listings.map((l) => (
          <div
            key={l.listing_id}
            className="bg-white/80 border border-sage/20 rounded-2xl p-4 mb-3 flex flex-wrap items-center gap-3 shadow-sm cursor-pointer hover:border-sage/50 hover:shadow-md transition"
            data-testid={`pending-listing-${l.listing_id}`}
            onClick={() => openContentDetail("listing", l)}
            role="button"
            tabIndex={0}
            onKeyDown={(ev) => openOnKey(ev, "listing", l)}
          >
            {(l.photos || []).length > 0 && (
              <img
                src={fileHref(l.photos[0])}
                alt={l.product_name}
                className="w-14 h-14 rounded-xl object-cover border border-sage/20 shrink-0"
                data-testid={`pending-listing-photo-${l.listing_id}`}
              />
            )}
            <div className="flex-1 min-w-[200px]">
              <p className="font-bold text-forest text-sm">{l.product_name}</p>
              <p className="text-xs text-forest/60">by {l.seller_name} · {l.category} · {l.price || "—"}</p>
              <p className="text-[11px] text-sage-dark/70 font-semibold mt-1">Tap card to view full listing →</p>
            </div>
            <div className="flex gap-2 shrink-0" onClick={(ev) => ev.stopPropagation()}>
              <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => reviewListing(l.listing_id, "approve")} data-testid={`approve-listing-${l.listing_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => reviewListing(l.listing_id, "reject")} data-testid={`reject-listing-${l.listing_id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
